/**
 * API Client
 * Core request handling with retry logic and authentication
 */

import { getApiUrl } from '../../../../../config/environment';
import { getSession } from '../../../../../core/auth/supabase-auth';
import { TrainingApiResponse, ApiError, RetryConfig } from '../types';

// Lazy getter to avoid circular dependency issues in production builds
export const getApiBaseUrl = () => getApiUrl('/api/training');

/**
 * Retry configuration
 */
export const RETRY_CONFIG: RetryConfig = {
  maxAttempts: 3,
  baseDelay: 1000, // 1 second
  maxDelay: 10000, // 10 seconds
  backoffFactor: 2
};

/**
 * Determine if error should trigger a retry
 */
export function shouldRetry(error: unknown): boolean {
  const e = error as Record<string, unknown>;

  // Network errors
  if (e.name === 'TypeError' && typeof e.message === 'string' && e.message.includes('fetch')) {
    return true;
  }

  // Server errors (5xx)
  if (typeof e.statusCode === 'number' && e.statusCode >= 500) {
    return true;
  }

  // Timeout errors
  if (e.name === 'AbortError') {
    return true;
  }

  return false;
}

/**
 * Make API request with retry logic
 */
export async function makeRequest<T>(
  url: string,
  options: RequestInit = {},
  retryCount: number = 0
): Promise<TrainingApiResponse<T>> {
  try {
    // Get authentication token from Supabase session
    const session = await getSession();
    const accessToken = session?.access_token;

    if (!accessToken) {
      throw new Error('No authentication token available. Please log in.');
    }

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        ...options.headers
      },
      ...options
    });

    const data = await response.json();

    if (!response.ok) {
      console.error('API Error Response:', {
        status: response.status,
        statusText: response.statusText,
        data
      });
      const apiError: ApiError = new Error(data.error || data.message || 'API request failed');
      apiError.statusCode = response.status;
      apiError.code = data.error_code;
      apiError.details = data;
      apiError.recoverySuggestions = data.recovery_suggestions || [];
      throw apiError;
    }

    return data;
  } catch (error) {
    // Retry logic for network errors
    if (retryCount < RETRY_CONFIG.maxAttempts - 1 && shouldRetry(error)) {
      const delay = Math.min(
        RETRY_CONFIG.baseDelay * Math.pow(RETRY_CONFIG.backoffFactor, retryCount),
        RETRY_CONFIG.maxDelay
      );

      await new Promise(resolve => setTimeout(resolve, delay));
      return makeRequest<T>(url, options, retryCount + 1);
    }

    throw error;
  }
}

/**
 * Make authenticated request without retry (for specific use cases)
 */
export async function makeAuthenticatedRequest(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const session = await getSession();
  const accessToken = session?.access_token;

  if (!accessToken) {
    throw new Error('No authentication token available. Please log in.');
  }

  return fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      ...options.headers
    },
    ...options
  });
}
